'use client';
import { useEffect, useId, useRef, useState } from 'react';
import { useSavePluginConfig } from './mutations';
import { ElowenApiError, isUnconfirmedWrite } from './elowenClient';
import { useAutosaveToast } from '../components/ui/Toast';
import { classifyFailure, combineSaveFeedback, type SaveFailure, type SaveFailureKind } from './saveFeedback';
import { AUTOSAVE_UNCHANGED, useAutoSaveStatus, type AutoSaveClassification, type SaveStatus } from './useAutoSaveStatus';
import type { PluginConfigField, PluginConfigSaveResponse } from './types';
import { isStepAligned } from './pluginNumber';

/** Invalid JSON remains editable but makes the save fail visibly; claiming "Saved" while dropping
 * that field would lose the user's draft on navigation. */
class PluginConfigValidationError extends Error {}

/** Does this number field's draft value sit outside the manifest's `min`/`max`/`step`? The daemon
 *  enforces exactly these bounds on the write path and answers 400, so a value that fails here is
 *  never sent: the row shows the allowed range instead of the footer reporting a failed save. A
 *  cleared value (null) is the explicit reset to the default and always passes. */
export function numberOutOfBounds(field: PluginConfigField, value: unknown): boolean {
  if (field.type !== 'number' || value === null || value === undefined) return false;
  if (typeof value !== 'number' || !Number.isFinite(value)) return true;
  if (field.min !== undefined && value < field.min) return true;
  if (field.max !== undefined && value > field.max) return true;
  if (field.step !== undefined) {
    if (!isStepAligned(value, field.min ?? 0, field.step)) return true;
  }
  return false;
}

/** Shared by Setup's checklist and the editor's disclosure; stored secrets count as complete. */
export function requiredConfigFieldMissing(field: PluginConfigField, values: Record<string, unknown>, secretsSet: readonly string[]): boolean {
  if (!field.required) return false;
  const value = values[field.key] ?? field.default;
  if (field.type === 'secret' && secretsSet.includes(field.key)) return false;
  return value == null || String(value).trim() === '';
}

/** `validateAgainst` is the last persisted snapshot: like the daemon, a value is validated only when it
 *  differs from what is stored, so a legacy out-of-range value never blocks saving the other fields.
 *  Omit it to build a baseline without validating. */
function sanitizeConfig(values: Record<string, unknown>, schema: readonly PluginConfigField[], includeSecrets: ReadonlySet<string> = new Set(), validateAgainst?: Record<string, unknown>): Record<string, unknown> {
  const jsonKeys = new Set(schema.filter((field) => field.type === 'json').map((field) => field.key));
  const secretKeys = new Set(schema.filter((field) => field.type === 'secret').map((field) => field.key));
  const numberFields = new Map(schema.filter((field) => field.type === 'number').map((field) => [field.key, field]));
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (secretKeys.has(key) && !includeSecrets.has(key)) continue;
    const changed = validateAgainst !== undefined && !Object.is(value, validateAgainst[key]);
    if (changed && jsonKeys.has(key) && typeof value === 'string' && value.trim() !== '') {
      try { JSON.parse(value); } catch { throw new PluginConfigValidationError(`Invalid JSON in ${key}`); }
    }
    const numberField = numberFields.get(key);
    if (changed && numberField && numberOutOfBounds(numberField, value)) throw new PluginConfigValidationError(`Out-of-range value in ${key}`);
    out[key] = value;
  }
  return out;
}

function sameSnapshot(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The fields a save must actually SEND: those whose value no longer matches the last persisted snapshot.
 *  A save carrying the whole form would store the fields the editor merely DISPLAYS as the account's own —
 *  the per-account route applies a patch to the account's values, so a field the account only inherits
 *  stops following the instance default the first time anything else is saved. The `named` fields are the
 *  ones the caller is explicitly writing in a commit; they go out even when unchanged. */
function changedValues(
  values: Record<string, unknown>,
  persisted: Record<string, unknown>,
  named: readonly string[] = [],
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    const before = persisted[key];
    // Identity covers the primitives (undefined included); a list or a projects pick is compared by its
    // serialized form, which is how the draft holds it.
    if (!named.includes(key) && (value === before || JSON.stringify(value) === JSON.stringify(before))) continue;
    changed[key] = value;
  }
  return changed;
}

export interface PluginConfigCommitResult {
  /** The values are durable, but the live plugin registry has not activated them yet. */
  pending: boolean;
  /** Whether a write was made at all. `false` is a no-op: nothing differed from what is stored, so no request
   *  was sent and nothing was confirmed. It is not a successful persistence. */
  written: boolean;
}

type PluginConfigErrorKind = 'validation' | 'conflict' | 'transport';
interface PluginConfigConflict {
  config: Record<string, unknown>;
  secretsSet: string[];
  revision: number;
}

export interface PluginConfigDraft {
  values: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  /** Persist one replacement immediately and publish it to the draft only after the save succeeds. */
  commitValue: (key: string, value: unknown) => Promise<PluginConfigCommitResult>;
  /** Submit tracked changes and explicitly named values in one revision-checked patch. */
  commitValues: (values: Record<string, unknown>) => Promise<PluginConfigCommitResult>;
  status: ReturnType<typeof useAutoSaveStatus>['status'];
  errorKind: PluginConfigErrorKind | null;
  conflict: PluginConfigConflict | null;
  retry: () => Promise<void>;
  flush: () => Promise<ReturnType<typeof useAutoSaveStatus>['status']>;
  resolveConflict: (choice: 'reload' | 'merge') => void;
  ready: boolean;
  /** This draft INSTANCE's identity as a reporter: stable for its lifetime and unique among live drafts, even
   *  two of the same plugin and mode. A modal editor of one of its fields claims it with `useOwnerHome`, so
   *  this draft's recovery cards, and only its, are raised inside that dialog. */
  owner: string;
}

/** The daemon's description of the current state behind a revision conflict, when it sent a usable one. */
function readConflict(error: unknown, schema: readonly PluginConfigField[]): PluginConfigConflict | null {
  if (!(error instanceof ElowenApiError) || error.status !== 409) return null;
  const current = error.details?.current;
  if (!current || typeof current !== 'object' || Array.isArray(current)) return null;
  const { revision, config, secretsSet } = current as { revision?: unknown; config?: unknown; secretsSet?: unknown };
  if (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0) return null;
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
  if (!Array.isArray(secretsSet) || !secretsSet.every((key): key is string => typeof key === 'string')) return null;
  return { config: sanitizeConfig(config as Record<string, unknown>, schema), secretsSet, revision };
}

/** One draft shared by the schema form and live preview. Refetches after saving never re-seed a draft
 * that holds an unsaved edit, preventing a slow query invalidation from overwriting a newer in-progress
 * edit; a clean draft adopts a newer revision another editor of the same plugin stored.
 *
 * Secret fields have an explicit commit boundary: `setValue` refuses to put them in the autosaved
 * snapshot, while `commitValue` sends one replacement (or `null`, which erases the stored secret) and
 * removes the plaintext from the local draft after acceptance. The editor calls it on blur or Enter.
 * Other fields autosave only their changes against the last persisted snapshot. Explicit mode disables
 * autosave and teardown writes; commitValues is its only write boundary. Ambiguous acceptance locks writes
 * until the caller reloads an authoritative snapshot and remounts the draft.
 *
 * The draft is the ONE reporter of its own outcomes, automatic AND explicit (a secret commit): it feeds the
 * toast seam (`useAutosaveToast`) with both, so a consumer never toasts a commit it awaited. Validation stays
 * with the field (`errorKind`, `numberOutOfBounds`); every Reload calls the editor's authoritative `reload`.
 * A described conflict additionally offers merge; an ambiguous write offers only reload. A no-op writes nothing:
 * an autosave pass or a commit with nothing different from the stored snapshot sends no request and settles
 * no attempt. */
export function usePluginConfigDraft(
  name: string,
  detail: { config: Record<string, unknown>; configSchema: readonly PluginConfigField[]; revision?: number },
  options: {
    saveMode?: 'auto' | 'explicit';
    save?: (v: { name: string; values: Record<string, unknown>; expectedRevision?: number }) => Promise<unknown>;
    /** Every recovery Reload awaits this authoritative read. Preserve the mounted draft on rejection and
     *  remount only after success. Unknown writes and undescribed conflicts cannot merge or retry blindly. */
    reload: () => void | Promise<void>;
  },
): PluginConfigDraft {
  const instanceSave = useSavePluginConfig();
  const save = options.save ?? ((v: { name: string; values: Record<string, unknown>; expectedRevision?: number }) => instanceSave.mutateAsync(v));
  const explicit = options.saveMode === 'explicit';
  const [values, setValues] = useState<Record<string, unknown>>(() => sanitizeConfig(detail.config, detail.configSchema));
  const [commit, setCommit] = useState<{ status: SaveStatus; attempt: number; failure: SaveFailure | null }>({ status: 'idle', attempt: 0, failure: null });
  const commitOwner = useId();
  const resolveRef = useRef<(choice: 'reload' | 'merge') => void>(() => {});
  if (typeof options?.reload !== 'function') throw new Error('usePluginConfigDraft needs the editor\'s reload: the recovery of a conflict or of a write that may have been applied.');
  const reloadRef = useRef(options.reload);
  reloadRef.current = options.reload;
  // Server responses are not local edits: only setValue advances a field's edit version.
  const fieldEdits = useRef(new Map<string, number>());
  const [errorKind, setErrorKind] = useState<PluginConfigErrorKind | null>(null);
  const [conflict, setConflict] = useState<PluginConfigConflict | null>(null);
  const revision = useRef<number | undefined>(detail.revision);
  // A synchronous barrier protects queued writes and retained callbacks after conflicts or lost acceptance.
  const blockedWrite = useRef<{ error: unknown; unconfirmed: boolean } | null>(null);
  const [seededName, setSeededName] = useState<string>(() => name);
  // Serialize config patches so a slow older response can never land after a
  // newer one and roll the server back while the UI reports the latest generation as saved.
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const lastPersisted = useRef<Record<string, unknown>>(sanitizeConfig(detail.config, detail.configSchema));

  useEffect(() => {
    if (seededName === name) return;
    setValues(sanitizeConfig(detail.config, detail.configSchema));
    setCommit({ status: 'idle', attempt: 0, failure: null });
    fieldEdits.current.clear();
    revision.current = detail.revision;
    blockedWrite.current = null;
    lastPersisted.current = sanitizeConfig(detail.config, detail.configSchema);
    setConflict(null);
    setErrorKind(null);
    setSeededName(name);
  }, [detail.config, detail.configSchema, detail.revision, name, seededName]);

  const ready = seededName === name;
  const secretKeys = new Set(detail.configSchema.filter((field) => field.type === 'secret').map((field) => field.key));
  // A 400 is the daemon refusing a VALUE (its validator mirrors the bounds checked above), not a
  // transport failure: retrying the same snapshot can only fail again, so it reads as validation.
  const classifyError = (error: unknown): PluginConfigErrorKind => {
    if (error instanceof ElowenApiError) return error.status === 409 ? 'conflict' : error.status === 400 ? 'validation' : 'transport';
    return error instanceof PluginConfigValidationError ? 'validation' : 'transport';
  };
  const captureConflict = (error: unknown): void => {
    if (!(error instanceof ElowenApiError) || error.status !== 409) return;
    blockedWrite.current = { error, unconfirmed: false };
    const described = readConflict(error, detail.configSchema);
    if (described) setConflict(described);
  };
  // Reload always belongs to the editor: it rereads the whole domain and discards local secret inputs only
  // after success. A described conflict additionally permits merging the known nonsecret snapshot.
  const recoveryFor = (kind: SaveFailureKind, error: unknown): { reload: () => void | Promise<void>; merge?: () => void | Promise<void> } => {
    if (kind === 'conflict' && readConflict(error, detail.configSchema)) {
      return { reload: () => reloadRef.current(), merge: () => resolveRef.current('merge') };
    }
    return { reload: () => reloadRef.current() };
  };
  const classifyAutosave = (error: unknown): AutoSaveClassification => {
    const kind = classifyError(error);
    if (kind === 'validation') return { kind: 'validation' };
    if (kind === 'conflict') return { kind: 'conflict', recovery: recoveryFor('conflict', error) };
    return isUnconfirmedWrite(error) || blockedWrite.current?.unconfirmed
      ? { kind: 'unconfirmed', recovery: recoveryFor('unconfirmed', error) }
      : { kind: 'operation' };
  };
  const applyCanonical = (snapshot: Record<string, unknown>, response: unknown): void => {
    if (!response || typeof response !== 'object') return;
    const result = response as Partial<PluginConfigSaveResponse>;
    if (typeof result.revision === 'number') revision.current = result.revision;
    if (!result.config || typeof result.config !== 'object' || Array.isArray(result.config)) return;
    const canonical = sanitizeConfig(result.config as Record<string, unknown>, detail.configSchema);
    lastPersisted.current = canonical;
    setValues((current) => current === snapshot && !sameSnapshot(current, canonical) ? canonical : current);
  };
  const queueSave = (snapshot: Record<string, unknown>, committed = false): Promise<unknown> => {
    const operation = saveChain.current.then(async () => {
      if (blockedWrite.current) throw blockedWrite.current.error;
      if (committed) setCommit((current) => ({ ...current, status: 'saving', failure: null }));
      try {
        return await save({
          name,
          values: snapshot,
          ...(revision.current === undefined ? {} : { expectedRevision: revision.current }),
        });
      } catch (error) {
        captureConflict(error);
        // Only an HTTP refusal proves non-acceptance. A lost/5xx response may follow a durable write, and so may
        // any write the transport reports as unconfirmed, automatic or explicit: nothing resends it until the
        // editor has been read again.
        if (isUnconfirmedWrite(error) || (committed && !(error instanceof ElowenApiError && error.status >= 400 && error.status < 500))) {
          blockedWrite.current = { error, unconfirmed: true };
        }
        throw error;
      }
    });
    // Keep serialization alive after a rejected write without manufacturing an unhandled sibling promise.
    saveChain.current = operation.then(() => undefined, () => undefined);
    return operation;
  };
  const autosave = useAutoSaveStatus(
    [values],
    async () => {
      if (blockedWrite.current) throw blockedWrite.current.error;
      let snapshot: Record<string, unknown>;
      try {
        snapshot = sanitizeConfig(changedValues(values, lastPersisted.current), detail.configSchema, new Set(), lastPersisted.current);
      } catch (error) { setErrorKind(classifyError(error)); throw error; }
      // Nothing differs from what is stored: no request, no attempt, and a stale refusal of an edit that is gone ends.
      if (Object.keys(snapshot).length === 0) { setErrorKind(null); return AUTOSAVE_UNCHANGED; }
      setErrorKind(null);
      try {
        const response = await queueSave(snapshot);
        // Only what this save sent is persisted: the rest of the draft is whatever the last read (or the
        // response's own canonical view) said, and comparing against it is what keeps an inherited field
        // out of the next payload.
        lastPersisted.current = { ...lastPersisted.current, ...snapshot };
        applyCanonical(values, response);
        return response;
      } catch (error) {
        setErrorKind(classifyError(error));
        throw error;
      }
    },
    { ready: ready && !explicit, delay: 900, classify: classifyAutosave, reload: () => reloadRef.current() },
  );

  // Two editors of one plugin can be mounted at once: Settings → Access next to the plugin's own form, or
  // a plugin page under the Settings overlay. When the other one moves the shared query to a newer
  // revision, adopt it while this draft holds no unsaved edit. An edit in progress (or a save in flight,
  // whose snapshot is not persisted yet) keeps its revision, so its save still meets the conflict choice.
  useEffect(() => {
    if (seededName !== name || detail.revision === undefined || revision.current === undefined || detail.revision <= revision.current) return;
    if (blockedWrite.current || !sameSnapshot(sanitizeConfig(values, detail.configSchema), lastPersisted.current)) return;
    revision.current = detail.revision;
    lastPersisted.current = sanitizeConfig(detail.config, detail.configSchema);
    setValues(sanitizeConfig(detail.config, detail.configSchema));
    autosave.reset();
  }, [autosave, detail.config, detail.configSchema, detail.revision, name, seededName, values]);

  // Automatic saves and explicit commits are one owner's outcomes: reported once, here.
  // The instance id keeps two live drafts of one plugin apart; the surface still groups them into one saved card.
  const instance = useId();
  const owner = `${name}:${options.save ? 'personal' : 'instance'}:${instance}`;
  useAutosaveToast(
    combineSaveFeedback(autosave, { status: commit.status, editing: false, attempt: commit.attempt, failures: commit.failure ? [commit.failure] : [] }),
    { resource: owner },
  );

  const resolveConflict = (choice: 'reload' | 'merge'): void => {
    if (!conflict) return;
    // Only locally changed fields win. An untouched value from the old snapshot must not overwrite
    // a concurrent edit or resurrect a field the remote account configuration removed.
    const changed = Object.fromEntries(Object.entries(values).filter(([key, value]) =>
      JSON.stringify(value) !== JSON.stringify(lastPersisted.current[key])));
    const next = choice === 'reload' ? conflict.config : { ...conflict.config, ...changed };
    revision.current = conflict.revision;
    blockedWrite.current = null;
    lastPersisted.current = sanitizeConfig(conflict.config, detail.configSchema);
    setValues(next);
    setConflict(null);
    setErrorKind(null);
    setCommit((current) => ({ ...current, status: 'idle', failure: null }));
    // A reload adopts the daemon's state as the baseline. A merge keeps the local edits as a change still to be
    // written against that state, so the baseline must not swallow them.
    if (choice === 'reload') autosave.reset(); else autosave.cancel();
  };
  resolveRef.current = resolveConflict;

  const commitValues = async (committed: Record<string, unknown>): Promise<PluginConfigCommitResult> => {
    const blocked = blockedWrite.current;
    if (blocked) throw blocked.error;
    const base = values;
    const editVersions = new Map(fieldEdits.current);
    // The batch includes unsaved edits, so cancel their older debounce/queue instead of writing twice.
    // An already running save must settle before validation and the next revision-checked patch.
    autosave.cancel();
    const flushed = await autosave.flush();
    const blockedAfterFlush = blockedWrite.current;
    if (blockedAfterFlush) throw blockedAfterFlush.error;
    if (flushed === 'error' || flushed === 'unconfirmed') throw new Error('Resolve the failed config save before committing another value');
    setErrorKind(null);
    let response: unknown;
    let snapshot: Record<string, unknown>;
    // A secret cannot be compared with what is stored, so naming it is always a write; every other value
    // goes out only when it differs. A commit with nothing to send makes no request and settles nothing.
    const named = Object.keys(committed).filter((key) => secretKeys.has(key));
    try {
      snapshot = sanitizeConfig(
        changedValues({ ...base, ...committed }, lastPersisted.current, named),
        detail.configSchema, new Set(named), lastPersisted.current,
      );
      if (Object.keys(snapshot).length === 0) return { pending: false, written: false };
      response = await queueSave(snapshot, true);
    } catch (error) {
      setErrorKind(classifyError(error));
      // The same classifier as the automatic path: one reading of a rejection, never a second copy.
      const classified = classifyFailure(error, { reload: () => reloadRef.current(), classify: classifyAutosave });
      setCommit((current) => {
        const attempt = current.attempt + 1;
        return {
          status: classified.kind === 'unconfirmed' ? 'unconfirmed' : 'error', attempt,
          failure: { owner: commitOwner, attempt, cause: error, ...classified } as SaveFailure,
        };
      });
      throw error;
    }
    const canonical = sanitizeConfig(
      response && typeof response === 'object' && 'config' in response
        && response.config && typeof response.config === 'object' && !Array.isArray(response.config)
        ? response.config as Record<string, unknown> : { ...lastPersisted.current, ...snapshot },
      detail.configSchema,
    );
    if (response && typeof response === 'object' && 'revision' in response && typeof response.revision === 'number') revision.current = response.revision;
    lastPersisted.current = canonical;
    setValues((current) => ({ ...canonical, ...Object.fromEntries(Object.entries(current).filter(([key]) =>
      fieldEdits.current.get(key) !== editVersions.get(key))) }));
    const pending = typeof response === 'object' && response !== null && 'pending' in response && response.pending === true;
    setCommit((current) => ({ status: pending ? 'pending' : 'saved', attempt: current.attempt + 1, failure: null }));
    return { pending, written: true };
  };

  return {
    values,
    // Secret values belong to the explicit replacement UI, never to the generic draft controller.
    setValue: (key, value) => {
      if (secretKeys.has(key)) return;
      fieldEdits.current.set(key, (fieldEdits.current.get(key) ?? 0) + 1);
      setValues((current) => ({ ...current, [key]: value }));
    },
    commitValue: (key, value) => commitValues({ [key]: value }),
    commitValues,
    status: blockedWrite.current?.unconfirmed ? 'unconfirmed' : errorKind === 'conflict' ? 'error' : explicit ? commit.status : autosave.status,
    errorKind,
    conflict,
    retry: autosave.retry,
    flush: explicit ? async () => commit.status : autosave.flush,
    resolveConflict,
    ready,
    owner,
  };
}
